import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  infiniteQueryOptions,
  keepPreviousData,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import type { PaginatedOut } from "./types/masterData"

// ─── Shared cursor-paginated list query ────────────────────────────────────────

// `key` is caller-supplied and already encodes url + params (each call site
// passes its own literal key including `params`), so the queryFn closing over
// `url`/`params` directly is safe despite not appearing in the key literal here.
export function mcCursorList<TRow>(
  key: readonly unknown[],
  url: string,
  params: Record<string, unknown> = {},
  enabled = true,
) {
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- key is caller-supplied, already encodes url+params
  return infiniteQueryOptions({
    queryKey: key,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      apiClientV2
        .get<PaginatedOut<TRow>>(url, {
          params: { ...params, cursor: pageParam ?? undefined },
        })
        .then((r) => ({
          // A 202 (or any 2xx) with an empty body resolves `r.data` to `""`,
          // not the paginated shape — axios treats it as success regardless
          // of body. Every caller here flatMaps `.items` across pages with
          // no per-page null check (see useFacetOptions.ts), so a malformed
          // page crashes the first `.map`/`.filter` downstream instead of
          // rendering empty. Same fix as declarationsApi/supplierInquiriesApi.
          items: r.data?.items ?? [],
          total: r.data?.total ?? 0,
          nextCursor: r.data?.nextCursor ?? null,
        })),
    getNextPageParam: (last: PaginatedOut<TRow>) =>
      last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
  })
}

// Same as mcCursorList, but keeps the previous page's rows on screen while a
// new search/filter combination loads instead of flashing to the loading
// skeleton. Only use this for top-level lists whose queryKey doesn't include
// an entity id — for per-entity sub-resources (audit trail, BOM children,
// etc.) keeping stale data on screen means the previous entity's rows can
// render under the newly-selected entity's header while the real fetch is
// still in flight.
export function mcCursorListKeepPrevious<TRow>(
  key: readonly unknown[],
  url: string,
  params: Record<string, unknown> = {},
  enabled = true,
) {
  return {
    ...mcCursorList<TRow>(key, url, params, enabled),
    placeholderData: keepPreviousData,
  }
}

// ─── Shared selection resolve: POST ids, chunked ─────────────────────────────

/**
 * Cheap, stable identity for an id LIST used inside a query key.
 *
 * A checkbox selection can hold tens of thousands of ids, and react-query
 * hashes the whole key on every render — a raw id array would serialize
 * hundreds of KB each time. Two independent 32-bit hashes over the ids, in ONE
 * pass (length included, separator folded in so ["ab","c"] and ["a","bc"]
 * differ, order kept because the response's order follows the request's),
 * yield a short key. Call it inside a `useMemo` keyed on the array, never
 * inline on every render.
 *
 * Why two: a collision means one selection is answered with another's
 * suppliers, and because both consumers filter the response against the live
 * `selectedIds`, that surfaces as a SHORT recipient list, a supplier who is
 * simply never asked. Filtering cannot see it and neither can the send gate
 * (`isCovered` in `useArticleSupplierGroups`), since a collision sits under
 * the current selection's own key and reads as a settled response for it. So
 * the key itself has to make it unreachable. At 64 bits two different
 * selections of the same length collide with probability about 1 in 18
 * quintillion (2^-64), and a session producing 1,000 distinct selections
 * faces roughly 3 in 100 trillion. The single 32-bit hash this replaces was 1
 * in 4.3 billion per pair, and walking ids of the form "art-<n>" through it
 * finds its first collision after ~484,000 of them: inside one org's article
 * table.
 */
export function fingerprintIds(ids: readonly string[]): string {
  // FNV-1a, plus a second mix with a different basis, a different prime and a
  // rotate, so the two do not move together.
  let h1 = 0x811c9dc5
  let h2 = 0xcbf29ce4
  for (const id of ids) {
    for (let i = 0; i < id.length; i++) {
      const code = id.charCodeAt(i)
      h1 = Math.imul(h1 ^ code, 0x01000193)
      h2 = Math.imul(h2 ^ code, 0x85ebca6b)
      h2 = (h2 << 13) | (h2 >>> 19)
    }
    h1 = Math.imul(h1 ^ 0x2c, 0x01000193)
    h2 = Math.imul(h2 ^ 0x2c, 0x85ebca6b)
    h2 = (h2 << 13) | (h2 >>> 19)
  }
  return `${ids.length}:${(h1 >>> 0).toString(36)}:${(h2 >>> 0).toString(36)}`
}

/**
 * POST `ids` to a resolve endpoint in chunks under its server-side cap and
 * concatenate the responses. Sequential on purpose: a selection resolves in a
 * handful of requests, and firing them all at once would only move the spike.
 * Every resolve endpoint keeps the request's id order, so concatenation
 * preserves it across chunks.
 */
export async function postIdsInChunks<TRow>(
  url: string,
  idsField: string,
  ids: readonly string[],
  chunkSize: number,
  signal?: AbortSignal,
): Promise<TRow[]> {
  const rows: TRow[] = []
  for (let start = 0; start < ids.length; start += chunkSize) {
    // Superseded by a newer selection: stop before spending another request,
    // and throw rather than return the chunks resolved so far — a partial
    // answer would cache as if it were the whole selection.
    signal?.throwIfAborted()
    const chunk = ids.slice(start, start + chunkSize)
    // The request interceptor snake_cases body keys, so `articleIds` arrives
    // as `article_ids` — the field name the endpoint declares. `signal` is
    // axios's own cancellation option, so the in-flight chunk is dropped too,
    // not just the ones that would follow it.
    const response = await apiClientV2.post<TRow[]>(
      url,
      { [idsField]: chunk },
      { signal },
    )
    if (Array.isArray(response.data)) rows.push(...response.data)
  }
  return rows
}

// ─── Shared entity patch mutation: list + detail + audit invalidation ────────

/**
 * Drop the caches one master-data entity's surfaces read, after a write.
 *
 * Exported because the agent chat writes from OUTSIDE React's render tree, where
 * there is no `onSuccess` to hang this on, and a mounted detail panel does not
 * refetch on its own — skipping this reads as the write having failed. Shared
 * with {@link useMcPatch} below rather than duplicated there, so the hook path
 * and the chat path can never drift on which keys a write touches.
 *
 * The audit key is not optional: every master-data write inserts an
 * `mc_compliance_events` row, so a stale audit tab is the one panel that
 * visibly lies right after an agent edit — and the one someone opens *because*
 * the agent made the change.
 */
export function invalidateMcEntityCaches(
  qc: QueryClient,
  entity: string,
  id: string,
): void {
  qc.invalidateQueries({ queryKey: ["mc", entity, "list"] })
  // "How many of these match" (`useCountMatchingArticles`) is subtracted from the
  // list total just refreshed above; a stale one skews "Add all (n)".
  qc.invalidateQueries({ queryKey: ["mc", entity, "count-matching"] })
  qc.invalidateQueries({ queryKey: ["mc", entity, "detail", id] })
  qc.invalidateQueries({ queryKey: ["mc", entity, id, "audit"] })
  // Org-wide completeness counts fold in every entity, so any field write can
  // move them (filling a supplier's last missing field empties a bucket).
  qc.invalidateQueries({ queryKey: ["mc", "master-data", "stats"] })
  // Same reasoning as the stats key just above: the "Datenlücken" tile's
  // counts (TUR-1539) are also computed org-wide from master data, so any
  // field write can move a gap bucket (e.g. filling an article's last
  // missing supplier empties it).
  qc.invalidateQueries({ queryKey: ["mc", "master-data", "data-gaps"] })
}

export function useMcPatch<TOut, TIn>(entity: string, base: string) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: TIn }) =>
      apiClientV2.patch<TOut>(`${base}/${id}`, changes).then((r) => r.data),
    onSuccess: (_, { id }) => invalidateMcEntityCaches(qc, entity, id),
  })
  // All entity patches (suppliers/articles/customers/products) share the
  // same MC_MASTER_DATA_EDIT gate on the backend.
  return useGuardedMutation(mutation, PERMISSIONS.MC_MASTER_DATA_EDIT)
}
